import MAP_BOUNDS from "./mapbounds";
import { worldToChunk } from "./mapcoordinates";
import { CollisionDetector } from "../helpers/collisiondetector";
import { Mesh as ZMSMesh } from "../loaders/zms";
import {
    Object3D,
    Vector3,
    Raycaster,
    AudioListener,
    Audio,
    AudioLoader,
    Mesh,
    BufferGeometry,
    BufferAttribute,
    Quaternion,
    Uint16BufferAttribute,
    RepeatWrapping,
    ClampToEdgeWrapping,
    LinearSRGBColorSpace,
    DataTexture,
    RGBAFormat,
    TextureLoader,
} from "three";
import { WaterEffect } from "./watereffect";
import { MultiWait } from "../util/multiwait";
import { TilemapData } from "../loaders/til";
import { HeightmapData } from "../loaders/him";
import { ZoneChunkData, ZoneWarpPoint } from "../loaders/ifo";
import { findWarpContact as locateWarpContact } from "./warppoints";
import { createWarpGateMesh } from "./warpgate";
import { LightmapManager } from "../datamanagers/lightmapmanager";
import { DataManager, GDM } from "../helpers/datamanager";
import { ShaderManager } from "../datamanagers/shadermanager";
import { TextureManager } from "../datamanagers/texturemanager";
import { ModelListManager } from "../datamanagers/modellistmanager";
import { ZoneData } from "../loaders/zon";
import { EffectManager } from "../effects/effectmanager";
import { ModelList } from "../loaders/zsc";
import { SETTINGS } from "../settings";
import { dataUrl } from "../util/path";
import {
    CompiledTerrainChunk,
    CompiledTerrainPackage,
    compiledChunkToRuntimeData,
    loadCompiledTerrainPackage,
} from "./compiledterrain";
import { MAP_SOURCE_MODE } from "./mapsource";

// Access global GAME_OPTIONS (set by core.ts or editor)
declare const GAME_OPTIONS: any;

const LOADSKY = true;
const LOADOBJECTS = true;
const LOADWATER = true;
const LOADEFFECTS = true;
const LOADTERRAIN = true;

// Feature flag: disable transparency for terrain materials to fix rendering artifacts
const TERRAIN_DISABLE_TRANSPARENCY = true;

export class MapManager {
    rootObj: Object3D;
    isLoaded: boolean;
    cnstModelMgr: any;
    decoModelMgr: any;
    basePath: any;
    chunks: Record<string, WorldChunk>;
    terChunks: any[];
    objects: any[];
    colObjects: any[];
    warpPoints: ZoneWarpPoint[];
    collisionDetector: any;
    zoneInfo: any;
    viewDistSq: number;
    DM: any;
    skyMat: any;
    dayNightCycle: number;
    dayNightDirection: number;
    dayNightEnabled: boolean;
    soundListener: any;
    sound: any;
    textures: any;
    compiledTerrain: CompiledTerrainPackage | null;

    constructor() {
        this.rootObj = new Object3D();
        this.rootObj.position.set(5200, 5200, 0);
        this.isLoaded = false;
        this.cnstModelMgr = null;
        this.decoModelMgr = null;
        this.basePath = null;
        this.chunks = {};
        this.terChunks = [];
        this.objects = [];
        this.colObjects = [];
        this.warpPoints = [];
        this.collisionDetector = new CollisionDetector(() => this.colObjects);
        this.zoneInfo = null;
        this.viewDistSq = Math.pow(300, 2);
        this.DM = new DataManager();
        this.skyMat = null;
        this.dayNightCycle = 0;
        this.dayNightDirection = 1;
        this.dayNightEnabled = true;
        this.soundListener = null;
        this.sound = null;
        this.compiledTerrain = null;
    }

    update(delta: number): void {
        if (this.skyMat && this.dayNightEnabled) {
            this.dayNightCycle += this.dayNightDirection * (delta / 75);
            if (this.dayNightCycle >= 1) {
                this.dayNightCycle = 1;
                this.dayNightDirection = -1;
            } else if (this.dayNightCycle <= 0) {
                this.dayNightCycle = 0;
                this.dayNightDirection = 1;
            }
            this.setDayNightCycle(this.dayNightCycle);
        }
        if (this.sound) {
            const volume = GAME_OPTIONS.get("musicVolumePercentage") / 100;
            this.sound.setVolume(volume);
        }
    }

    setDayNightCycle(value: number): void {
        this.dayNightCycle = Math.max(0, Math.min(1, value));

        if (this.skyMat) {
            this.skyMat.uniforms.blendRatio.value = this.dayNightCycle;
        }

        for (let i = 0; i < this.terChunks.length; i++) {
            const mesh = this.terChunks[i];
            if (mesh.material?.uniforms?.dayNightCycle) {
                mesh.material.uniforms.dayNightCycle.value = this.dayNightCycle;
            }
        }

        WaterEffect.setDayNight(this.dayNightCycle);

        for (let i = 0; i < this.objects.length; i++) {
            const obj = this.objects[i];
            obj.traverse((child: any) => {
                if (child.material?.uniforms?.dayNightCycle) {
                    child.material.uniforms.dayNightCycle.value = this.dayNightCycle;
                }
            });
        }

        const lightScale = 1.0 - this.dayNightCycle * 0.7;
        if (ambientLight) {
            ambientLight.intensity = Math.PI * lightScale;
        }
        if (directionalLight) {
            directionalLight.intensity = Math.PI * lightScale;
        }
    }

    addToScene(): void {
        scene.add(this.rootObj);
        this.rootObj.updateMatrixWorld(true);
    }

    removeFromScene(): void {
        scene.remove(this.rootObj);
        this.removeMusic();
    }

    rayPick(rayCaster: Raycaster): any {
        const intersects: any[] = [];
        for (let i = 0; i < this.colObjects.length; ++i) {
            __rayIntersect(this.colObjects[i], rayCaster, intersects);
        }
        intersects.sort(__descSort);
        if (intersects.length > 0) {
            return intersects[0];
        }
        return null;
    }

    findHighPoint(x: number, y: number, fromZ?: number): number | undefined {
        if (fromZ === undefined) {
            fromZ = SKY_HEIGHT;
        }
        let caster = new Raycaster(
            new Vector3(x, y, fromZ),
            new Vector3(0, 0, -1)
        );
        let pickInfo = this.rayPick(caster);
        if (pickInfo) {
            return pickInfo.point.z;
        }
        caster = new Raycaster(new Vector3(x, y, fromZ), new Vector3(0, 0, 1));
        pickInfo = this.rayPick(caster);
        if (!pickInfo) {
            console.warn(
                "Attempted to find high point in an unloaded chunk.",
                x,
                y,
                fromZ
            );
        } else {
            console.warn("Uhhhh... Someone fell through the floor...");
        }
        return undefined;
    }

    findWarpContact(position: { x: number; y: number }): ZoneWarpPoint | null {
        return locateWarpContact(position, this.rootObj.position, this.warpPoints);
    }

    findHighPointWithInfo(
        x: number,
        y: number,
        fromZ?: number
    ): { z: number; isTerrain: boolean } | undefined {
        if (fromZ === undefined) {
            fromZ = SKY_HEIGHT;
        }
        const caster = new Raycaster(
            new Vector3(x, y, fromZ),
            new Vector3(0, 0, -1)
        );
        const pickInfo = this.rayPick(caster);
        if (pickInfo) {
            let hitObject = pickInfo.object;
            let isTerrain = false;

            while (hitObject) {
                if (hitObject.name?.startsWith("TER_")) {
                    isTerrain = true;
                    break;
                }
                hitObject = hitObject.parent;
            }

            return { z: pickInfo.point.z, isTerrain };
        }
        return undefined;
    }

    initializeMusic(music: string, loop = true): void {
        if (SETTINGS.ENABLE_MUSIC && music) {
            this.soundListener = new AudioListener();
            this.sound = new Audio(this.soundListener);

            const volume =
                parseInt(
                    (localStorage.getItem(
                        "gamesettings_musicvolumepercentage"
                    ) || "50") as string
                ) / 100;
            this.sound.setVolume(volume);
            this.sound.setLoop(loop);

            const audioLoader = new AudioLoader();
            audioLoader.load(dataUrl(music), (buffer: any) => {
                this.sound.setBuffer(buffer);
                this.sound.play();
            });
        }
    }

    removeMusic(): void {
        const sound = this.sound;
        if (!sound) return;
        if (sound.source?.stop) {
            try {
                sound.source.stop(0);
            } catch (e) {}
        }
        if (typeof sound.setLoop === "function") sound.setLoop(false);
        if ("isPlaying" in sound) sound.isPlaying = false;
        if ("offset" in sound) sound.offset = 0;
        sound.source = null;
    }

    setMap(mapIdx: number, callback: () => void): void {
        if (!mapIdx) {
            console.warn("mapIdx is not set or 0, probably timing error");
        }
        loadCompiledTerrainPackage(
            mapIdx,
            MAP_SOURCE_MODE,
            config.compiledTerrainPath,
        ).then((compiledTerrain) => {
            this.compiledTerrain = compiledTerrain;
            if (compiledTerrain) {
                console.info(
                    `Using compiled map ${compiledTerrain.revision.slice(0, 12)} ` +
                    `for map ${mapIdx}`,
                );
            } else if (MAP_SOURCE_MODE === "auto") {
                console.info(`No compiled map for map ${mapIdx}; using legacy ROSE files`);
            }
            this._setMap(mapIdx, callback);
        }).catch((error) => {
            console.error(`Failed to select terrain source for map ${mapIdx}:`, error);
            if (typeof window !== "undefined" && typeof window.alert === "function") {
                window.alert(`Map ${mapIdx} failed to load:\n${error.message ?? error}`);
            }
        });
    }

    private _setMap(mapIdx: number, callback: () => void): void {
        this.textures = [];
        WaterEffect.waterPlanes = [];
        if (this.compiledTerrain) {
            this._setCompiledMap(callback);
            return;
        }
        GDM.get("list_zone", "list_sky", (zoneTable: any, skyTable: any) => {
            const mapRow = zoneTable.row(mapIdx);
            const skyIdx = mapRow[ZONE_TABLE.BG_IMAGE];

            this.initializeMusic(mapRow[ZONE_TABLE.BG_MUSIC_DAY]);

            const skyRow = skyTable.row(skyIdx);
            if (!skyIdx || !skyRow) {
                console.error(
                    "Error loading sky",
                    skyIdx,
                    skyTable.row(skyIdx)
                );
                return;
            } else {
                if (LOADSKY) this.loadSky(skyRow);
            }

            const finishResourceLoad = (
                zone: ZoneData,
                cnstMdls: any,
                decoMdls: any,
            ) => {
                    const zoneFile = String(mapRow[ZONE_TABLE.FILE] ?? "");
                    const lastPathSlash = Math.max(
                        zoneFile.lastIndexOf("\\"),
                        zoneFile.lastIndexOf("/"),
                    );
                    this.basePath = zoneFile.substring(0, lastPathSlash + 1);
                    this.zoneInfo = zone;
                    this.cnstModelMgr = cnstMdls;
                    this.decoModelMgr = decoMdls;

                    const chunkBounds = getMapBounds(this.basePath);
                    const chunkSX = chunkBounds[0][0];
                    const chunkEX = chunkBounds[0][1];
                    const chunkSY = chunkBounds[1][0];
                    const chunkEY = chunkBounds[1][1];

                    for (let iy = chunkSY; iy <= chunkEY; ++iy) {
                        for (let ix = chunkSX; ix <= chunkEX; ++ix) {
                            this.chunks[`${ix}_${iy}`] = new WorldChunk(
                                this,
                                ix,
                                iy
                            );
                        }
                    }
                    this.isLoaded = true;

                    const waitAll = new MultiWait();
                    let chunkCount = 0;
                    for (const chunkIdx in this.chunks) {
                        if (this.chunks.hasOwnProperty(chunkIdx)) {
                            const chunk = this.chunks[chunkIdx];
                            chunk.load(waitAll.one());
                            chunkCount++;
                        }
                    }
                    waitAll.wait(() => {
                        callback();
                    });
            };

            this.DM.register(
                "cnstmdls",
                ModelListManager,
                mapRow[ZONE_TABLE.CNST_TABLE]
            );
            this.DM.register(
                "decomdls",
                ModelListManager,
                mapRow[ZONE_TABLE.OBJECT_TABLE]
            );
            this.DM.register("zoneinfo", ZoneData, mapRow[ZONE_TABLE.FILE]);
            this.DM.get(
                "zoneinfo",
                "cnstmdls",
                "decomdls",
                finishResourceLoad,
            );
        });
    }

    /** Load a published map without consulting LIST_ZONE, ZON, IFO, LIT, HIM, or TIL. */
    private _setCompiledMap(callback: () => void): void {
        const pkg = this.compiledTerrain!;
        this.basePath = null;
        this.zoneInfo = new ZoneData();
        this.cnstModelMgr = null;
        this.decoModelMgr = null;

        const music = pkg.visual.music?.audioPath || pkg.gameplay.settings.musicDay;
        if (music) this.initializeMusic(music, pkg.visual.music?.loop ?? true);
        const sky = pkg.visual.sky;
        if (LOADSKY && sky?.meshPath && sky.dayTexture && sky.nightTexture) {
            this.loadSky([sky.meshPath, sky.dayTexture, sky.nightTexture, sky.blendRatio]);
            this.dayNightCycle = sky.blendRatio;
        }

        for (const terrainChunk of pkg.chunks) {
            const chunk = new WorldChunk(this, 0, 0, terrainChunk);
            this.chunks[chunk.name] = chunk;
        }

        this.isLoaded = true;
        const waitAll = new MultiWait();
        for (const chunk of Object.values(this.chunks)) {
            chunk.load(waitAll.one());
        }
        this._loadCompiledVisuals(waitAll.one());
        waitAll.wait(callback);
    }

    private _applyCompiledTransform(object: Object3D, transform: {
        position: [number, number, number];
        quaternion: [number, number, number, number];
        scale: [number, number, number];
        visible: boolean;
    }): void {
        object.position.fromArray(transform.position).sub(this.rootObj.position);
        object.quaternion.fromArray(transform.quaternion);
        object.scale.fromArray(transform.scale);
        object.visible = transform.visible;
    }

    private _loadCompiledVisuals(callback: () => void): void {
        const pkg = this.compiledTerrain!;
        const visual = pkg.visual;
        const waitAll = new MultiWait();

        if (LOADOBJECTS) {
            for (const data of visual.staticObjects) {
                const objectWait = waitAll.one();
                ModelListManager.load(data.modelListPath, (manager: ModelListManager) => {
                    const lightmap = data.lightmap
                        ? new LightmapManager({
                            objects: { 0: data.lightmap.object },
                            textures: data.lightmap.textures,
                            embeddedTextures: data.lightmap.embeddedTextures,
                        })
                        : null;
                    const object = manager.createForStatic(data.objectId, lightmap, 0, objectWait);
                    if (object) {
                        object.name = data.name;
                        this._applyCompiledTransform(object, data);
                        object.updateMatrix();
                        object.matrixAutoUpdate = false;
                        this.rootObj.add(object);
                        this.objects.push(object);
                        this.colObjects.push(object);
                    }
                });
            }
        }

        if (LOADWATER) {
            WaterEffect.reset();
            for (const data of visual.waterPlanes) {
                const object = new Object3D();
                object.name = "COMPILED_WATER";
                object.add(new OceanBlock(
                    new Vector3().fromArray(data.start),
                    new Vector3().fromArray(data.end),
                ).load());
                this._applyCompiledTransform(object, data);
                this.rootObj.add(object);
            }
        }

        if (LOADEFFECTS) {
            for (const data of visual.effects) {
                const effectWait = waitAll.one();
                EffectManager.loadEffect(data.filePath, (effect: any) => {
                    if (effect) {
                        const object = new Object3D();
                        object.name = "COMPILED_EFFECT";
                        object.add(effect.rootObj);
                        if (effect.rootObj2) object.add(effect.rootObj2);
                        this._applyCompiledTransform(object, data);
                        this.rootObj.add(object);
                        effect.play();
                    }
                    effectWait();
                });
            }
        }

        if (visual.animations.length > 0) {
            const animationsWait = waitAll.one();
            GDM.get("morph_anims", (morphAnims: any) => {
                for (const data of visual.animations) {
                    const object = morphAnims.create(data.objectId);
                    object.name = "COMPILED_ANIMATION";
                    this._applyCompiledTransform(object, data);
                    this.rootObj.add(object);
                }
                animationsWait();
            });
        }

        for (const data of pkg.gameplay.warps) {
            const warp = new ZoneWarpPoint();
            warp.name = `COMPILED_WARP_${data.id}`;
            warp.warpId = data.id;
            warp.position = new Vector3(
                data.srcX - this.rootObj.position.x,
                data.srcY - this.rootObj.position.y,
                data.srcZ - this.rootObj.position.z,
            );
            warp.rotation = new Quaternion().fromArray(data.quaternion);
            warp.scale = new Vector3().fromArray(data.scale);
            this.warpPoints.push(warp);
            const gate = createWarpGateMesh();
            gate.position.copy(warp.position);
            gate.quaternion.copy(warp.rotation);
            gate.scale.copy(warp.scale);
            this.rootObj.add(gate);
        }

        waitAll.wait(callback);
    }

    loadSky(skyData: any): void {
        const mesh = skyData[0];
        const dayTexture = skyData[1];
        const nightTexture = skyData[2];
        if (skyObject) {
            skyScene.remove(skyObject);
            skyObject = null;
        }
        ZMSMesh.load(mesh, (geom: any) => {
            const texd = TextureManager.load(dayTexture);
            const texn = TextureManager.load(nightTexture);
            this.skyMat = ShaderManager.get("skydome").clone();
            this.skyMat.uniforms = {
                texture1: { type: "t", value: texd },
                texture2: { type: "t", value: texn },
                blendRatio: { type: "f", value: skyData[3] ?? 0.5 },
            };
            skyObject = new Mesh(geom, this.skyMat);
            skyScene.add(skyObject);
        });
    }

    setViewerInfo(pos: any, callback?: () => void): void {
        if (!this.isLoaded) {
            console.warn(
                "Attempted to load chunks before map was finished loading."
            );
            if (callback) callback();
            return;
        }
        if (callback) callback();
    }

    ensureChunkLoaded(
        worldX: number,
        worldY: number,
        callback: () => void
    ): void {
        if (!this.isLoaded) {
            console.warn("Map not loaded yet, deferring chunk load");
            setTimeout(
                () => this.ensureChunkLoaded(worldX, worldY, callback),
                100
            );
            return;
        }

        const { x: chunkX, y: chunkY } = worldToChunk(worldX, worldY);
        const chunkIdx = `${chunkX}_${chunkY}`;

        const chunk = this.chunks[chunkIdx];
        if (!chunk) {
            console.warn(`Chunk ${chunkIdx} does not exist, spawning anyway`);
            callback();
            return;
        }

        chunk.load(callback);
    }
}

const ZONE_TABLE = {
    NAME: 0,
    FILE: 1,
    START_POS: 2,
    REVIVE_POS: 3,
    IS_UNDERGROUND: 4,
    BG_MUSIC_DAY: 5,
    BG_MUSIC_NIGHT: 6,
    BG_IMAGE: 7,
    MINIMAP_NAME: 8,
    MINIMAP_STARTX: 9,
    MINIMAP_STARTY: 10,
    OBJECT_TABLE: 11,
    CNST_TABLE: 12,
    DAY_CYCLE: 13,
    MORNING_TIME: 14,
    DAY_TIME: 15,
    EVENING_TIME: 16,
    NIGHT_TIME: 17,
    PVP_STATE: 18,
    PLANET_NO: 19,
    TYPE: 20,
    CAMERA_TYPE: 21,
    JOIN_TRIGGER: 22,
    KILL_TRIGGER: 23,
    DEAD_TRIGGER: 24,
    SECTOR_SIZE: 25,
    STRING_ID: 26,
    WEATHER_TYPE: 27,
    PARTY_EXP_A: 28,
    PARTY_EXP_B: 29,
    RIDING_REFUSE_FLAG: 30,
    REVIVE_ZONENO: 31,
    REVIVE_X_POS: 32,
    REVIVE_Y_POS: 33,
};

function tileRotateUvs(tile: any, uv: any) {
    switch (tile.rotation) {
        case ZoneData.TILE_ROTATION.FLIP_HORIZONTAL:
            uv.x = 1.0 - uv.x;
            break;
        case ZoneData.TILE_ROTATION.FLIP_VERTICAL:
            uv.y = 1.0 - uv.y;
            break;
        case ZoneData.TILE_ROTATION.FLIP_BOTH:
            uv.x = 1.0 - uv.x;
            uv.y = 1.0 - uv.y;
            break;
        case ZoneData.TILE_ROTATION.CLOCKWISE_90:
            {
                const tmp = uv.x;
                uv.x = uv.y;
                uv.y = tmp;
            }
            break;
        case ZoneData.TILE_ROTATION.COUNTER_CLOCKWISE_90:
            {
                const tmp = uv.x;
                uv.x = uv.y;
                uv.y = 1.0 - tmp;
            }
            break;
    }
    return uv;
}

function __descSort(a: any, b: any) {
    return a.distance - b.distance;
}

function __rayIntersect(object: any, raycaster: any, intersects: any) {
    if (object.collisionMode === ModelList.Model.Part.COLLISION_MODE.NONE) {
        return;
    }
    object.raycast(raycaster, intersects);
    const children = object.children;
    for (let i = 0, l = children.length; i < l; i++) {
        __rayIntersect(children[i], raycaster, intersects);
    }
}

const SKY_HEIGHT = 1000;

function getMapBounds(mapBasePath: string) {
    let boundsName = mapBasePath.toUpperCase();
    boundsName = boundsName.substr("3DDATA\\MAPS\\".length);
    boundsName = boundsName.substr(0, boundsName.length - 1);
    boundsName = boundsName.replace("\\", "/");
    return MAP_BOUNDS[boundsName];
}

export class OceanBlock {
    start: Vector3;
    end: Vector3;

    constructor(start: Vector3, end: Vector3) {
        this.start = start.clone().multiplyScalar(ZZ_SCALE_IN);
        this.end   = end.clone().multiplyScalar(ZZ_SCALE_IN);
    }

    static update(delta: number): void {
        WaterEffect.update(delta);
    }

    load(): WaterEffect {
        return WaterEffect.createForPlane(
            this.start.x, this.start.y, this.start.z,
            this.end.x,   this.end.y
        );
    }
}

export class WorldChunk {
    static indicesBuffer: BufferAttribute | null = null;

    world: MapManager;
    name: string;
    chunkX: number;
    chunkY: number;
    textures: any;
    lightmapTex: any;
    info: any;
    heightmap: any;
    tilemap: any;
    position: Vector3;
    isVisible: boolean;
    loadState: number;
    loadWaiters: Array<() => void>;
    rootObj: Object3D;
    compiledTerrain: CompiledTerrainChunk | null;
    legacyChunkName: string | null;
    terrainTextures: string[] | null;
    terrainTiles: any[] | null;

    constructor(
        world: MapManager,
        chunkX: number,
        chunkY: number,
        compiledTerrain: CompiledTerrainChunk | null = null,
    ) {
        this.world = world;
        this.compiledTerrain = compiledTerrain;
        this.legacyChunkName = compiledTerrain?.legacyChunkName ?? null;
        this.name = this.legacyChunkName ?? compiledTerrain?.id ?? chunkX + "_" + chunkY;
        const parsedCoordinates = this.legacyChunkName?.match(/^(\d+)_(\d+)$/);
        this.chunkX = parsedCoordinates ? Number(parsedCoordinates[1]) : chunkX;
        this.chunkY = parsedCoordinates ? Number(parsedCoordinates[2]) : chunkY;
        this.textures = {};
        this.terrainTextures = null;
        this.terrainTiles = null;
        this.lightmapTex = null;
        this.info = null;
        this.heightmap = null;
        this.tilemap = null;
        this.position = compiledTerrain
            ? new Vector3(
                compiledTerrain.origin[0] - this.world.rootObj.position.x + 80,
                compiledTerrain.origin[1] - this.world.rootObj.position.y + 80,
                compiledTerrain.origin[2] - this.world.rootObj.position.z,
            )
            : new Vector3(
                (chunkX - 32) * 160,
                (32 - chunkY) * 160,
                0
            );
        this.isVisible = false;
        this.loadState = 0;
        this.loadWaiters = [];
        this.rootObj = new Object3D();
        this.rootObj.name = "CHUNK_" + this.name;
    }

    static getIndicesBuffer(): BufferAttribute {
        if (!WorldChunk.indicesBuffer) {
            const blockCountX = 16;
            const blockCountY = 16;
            const blockCount = blockCountX * blockCountY;
            const squarePerAxis = 4;
            const vertexPerAxis = squarePerAxis + 1;
            const squarePerBlock = squarePerAxis * squarePerAxis;
            const vertexPerBlock = vertexPerAxis * vertexPerAxis;
            const facePerSquare = 2;
            const indexPerFace = 3;

            const rawIndicesBuffer = new Uint16Array(
                blockCount * squarePerBlock * facePerSquare * indexPerFace
            );

            for (let blockY = 0; blockY < blockCountX; ++blockY) {
                for (let blockX = 0; blockX < blockCountY; ++blockX) {
                    const bgbIndex = blockY * blockCountY + blockX;
                    const vertBase = bgbIndex * vertexPerBlock;
                    const squareBase = bgbIndex * squarePerBlock;

                    for (let squareY = 0; squareY < squarePerAxis; ++squareY) {
                        for (
                            let squareX = 0;
                            squareX < squarePerAxis;
                            ++squareX
                        ) {
                            const vertex1 =
                                vertBase +
                                (squareY + 0) * vertexPerAxis +
                                (squareX + 0);
                            const vertex2 =
                                vertBase +
                                (squareY + 0) * vertexPerAxis +
                                (squareX + 1);
                            const vertex3 =
                                vertBase +
                                (squareY + 1) * vertexPerAxis +
                                (squareX + 0);
                            const vertex4 =
                                vertBase +
                                (squareY + 1) * vertexPerAxis +
                                (squareX + 1);

                            const squareIndex =
                                squareBase +
                                (squareY * squarePerAxis + squareX);
                            rawIndicesBuffer[
                                squareIndex * facePerSquare * indexPerFace + 0
                            ] = vertex1;
                            rawIndicesBuffer[
                                squareIndex * facePerSquare * indexPerFace + 1
                            ] = vertex2;
                            rawIndicesBuffer[
                                squareIndex * facePerSquare * indexPerFace + 2
                            ] = vertex3;
                            rawIndicesBuffer[
                                squareIndex * facePerSquare * indexPerFace + 3
                            ] = vertex4;
                            rawIndicesBuffer[
                                squareIndex * facePerSquare * indexPerFace + 4
                            ] = vertex3;
                            rawIndicesBuffer[
                                squareIndex * facePerSquare * indexPerFace + 5
                            ] = vertex2;
                        }
                    }
                }
            }
            WorldChunk.indicesBuffer = new BufferAttribute(rawIndicesBuffer, 1);
        }
        return WorldChunk.indicesBuffer;
    }

    private _getBlockTile(blockX: number, blockY: number): any {
        const tileIdx = (15 - blockY) * 16 + blockX;
        const tiles = this.terrainTiles ?? this.world.zoneInfo.tiles;
        return tiles[this.tilemap.map[tileIdx].number];
    }

    private _createLmOnlyMaterial(): any {
        const newMaterial = ShaderManager.get("terrain_lmonly").clone();
        newMaterial.uniforms = { texture1: { value: this.lightmapTex } };
        return newMaterial;
    }

    private _getTileTexture(texId: number): any {
        if (this.textures[texId]) {
            return this.textures[texId];
        }

        const texturePath = (this.terrainTextures ?? this.world.zoneInfo.textures)[texId];
        const newTexture = /\.dds$/i.test(texturePath)
            ? TextureManager.load(texturePath)
            : new TextureLoader().load(dataUrl(texturePath));
        newTexture.wrapS = RepeatWrapping;
        newTexture.wrapT = RepeatWrapping;
        newTexture.colorSpace = LinearSRGBColorSpace;
        this.textures[texId] = newTexture;
        return newTexture;
    }

    private _createMaterial(texId1: number, texId2: number): any {
        const tex1 = this._getTileTexture(texId1);
        const tex2 = this._getTileTexture(texId2);

        const newMaterial = ShaderManager.get("terrain").clone();
        newMaterial.texId1 = texId1;
        newMaterial.texId2 = texId2;
        newMaterial.uniforms = {
            texture1: { value: tex1 },
            texture2: { value: tex2 },
            texture3: { value: this.lightmapTex },
            dayNightCycle: { value: 0.0 },
        };

        if (TERRAIN_DISABLE_TRANSPARENCY) {
            newMaterial.transparent = false;
        }

        newMaterial.needsUpdate = true;
        return newMaterial;
    }

    private _buildTerrainBlock(
        blockX: number,
        blockY: number,
        bgbIdx: number,
        verts: Float32Array,
        uv0: Float32Array,
        uv1: Float32Array,
        uv2: Float32Array
    ): void {
        const tile = this._getBlockTile(blockX, blockY);
        const vertBase = bgbIdx * 5 * 5;

        for (let vy = 0; vy < 5; ++vy) {
            for (let vx = 0; vx < 5; ++vx) {
                const vertIdx = vertBase + (vy * 5 + vx);
                const vertX = blockX * 4 + vx;
                const vertY = 64 - (blockY * 4 + vy);
                verts[vertIdx * 3 + 0] = blockX * 10 + vx * 2.5;
                verts[vertIdx * 3 + 1] = blockY * 10 + vy * 2.5;
                verts[vertIdx * 3 + 2] =
                    this.heightmap.map[vertY * 65 + vertX] * ZZ_SCALE_IN;
                uv0[vertIdx * 2 + 0] = vx / 4;
                uv0[vertIdx * 2 + 1] = 1 - vy / 4;
                const tex2Uv = tileRotateUvs(tile, { x: vx / 4, y: vy / 4 });
                uv1[vertIdx * 2 + 0] = tex2Uv.x;
                uv1[vertIdx * 2 + 1] = 1 - tex2Uv.y;
                uv2[vertIdx * 2 + 0] = vertX / 64;
                uv2[vertIdx * 2 + 1] = vertY / 64;
            }
        }
    }

    private _buildTerrain(): void {
        const chunkGrps: any[] = [];
        const findChunkGrp = (tile: any) => {
            const texId1 = tile.layer1 + tile.offset1;
            const texId2 = tile.layer2 + tile.offset2;
            for (let i = 0; i < chunkGrps.length; ++i) {
                const chunkGrp = chunkGrps[i];
                if (chunkGrp.texId1 === texId1 && chunkGrp.texId2 === texId2) {
                    return chunkGrp;
                }
            }
            const newChunkGrp = { texId1, texId2, blocks: [] as any[] };
            chunkGrps.push(newChunkGrp);
            return newChunkGrp;
        };

        let materialOverride = null;
        if (!config.lmonly) {
            for (let by = 0; by < 16; ++by) {
                for (let bx = 0; bx < 16; ++bx) {
                    const tile = this._getBlockTile(bx, by);
                    const chunkGrp = findChunkGrp(tile);
                    chunkGrp.blocks.push({ x: bx, y: by });
                }
            }
        } else {
            materialOverride = this._createLmOnlyMaterial();
            const tile = this._getBlockTile(0, 0);
            const chunkGrp = findChunkGrp(tile);
            for (let by = 0; by < 16; ++by) {
                for (let bx = 0; bx < 16; ++bx) {
                    chunkGrp.blocks.push({ x: bx, y: by });
                }
            }
        }

        for (let i = 0; i < chunkGrps.length; ++i) {
            const chunkGrp = chunkGrps[i];
            const blockCount = chunkGrp.blocks.length;
            const verts = new Float32Array(blockCount * 5 * 5 * 3);
            const uv0 = new Float32Array(blockCount * 5 * 5 * 2);
            const uv1 = new Float32Array(blockCount * 5 * 5 * 2);
            const uv2 = new Float32Array(blockCount * 5 * 5 * 2);

            for (let j = 0; j < chunkGrp.blocks.length; ++j) {
                const block = chunkGrp.blocks[j];
                this._buildTerrainBlock(
                    block.x,
                    block.y,
                    j,
                    verts,
                    uv0,
                    uv1,
                    uv2
                );
            }

            const squarePerAxis = 4;
            const vertexPerAxis = squarePerAxis + 1;
            const squarePerBlock = squarePerAxis * squarePerAxis;
            const vertexPerBlock = vertexPerAxis * vertexPerAxis;
            const facePerSquare = 2;
            const indexPerFace = 3;

            const indices = new Uint16Array(
                blockCount * squarePerBlock * facePerSquare * indexPerFace
            );

            for (let blockIdx = 0; blockIdx < blockCount; ++blockIdx) {
                const vertBase = blockIdx * vertexPerBlock;
                const squareBase = blockIdx * squarePerBlock;

                for (let squareY = 0; squareY < squarePerAxis; ++squareY) {
                    for (let squareX = 0; squareX < squarePerAxis; ++squareX) {
                        const vertex1 =
                            vertBase +
                            (squareY + 0) * vertexPerAxis +
                            (squareX + 0);
                        const vertex2 =
                            vertBase +
                            (squareY + 0) * vertexPerAxis +
                            (squareX + 1);
                        const vertex3 =
                            vertBase +
                            (squareY + 1) * vertexPerAxis +
                            (squareX + 0);
                        const vertex4 =
                            vertBase +
                            (squareY + 1) * vertexPerAxis +
                            (squareX + 1);

                        const squareIndex =
                            squareBase + (squareY * squarePerAxis + squareX);
                        indices[
                            squareIndex * facePerSquare * indexPerFace + 0
                        ] = vertex1;
                        indices[
                            squareIndex * facePerSquare * indexPerFace + 1
                        ] = vertex2;
                        indices[
                            squareIndex * facePerSquare * indexPerFace + 2
                        ] = vertex3;
                        indices[
                            squareIndex * facePerSquare * indexPerFace + 3
                        ] = vertex4;
                        indices[
                            squareIndex * facePerSquare * indexPerFace + 4
                        ] = vertex3;
                        indices[
                            squareIndex * facePerSquare * indexPerFace + 5
                        ] = vertex2;
                    }
                }
            }

            const geometry = new BufferGeometry();
            geometry.setAttribute("position", new BufferAttribute(verts, 3));
            geometry.setIndex(new Uint16BufferAttribute(indices, 1));
            geometry.setAttribute("uv", new BufferAttribute(uv0, 2));
            geometry.setAttribute("uv1", new BufferAttribute(uv1, 2));
            geometry.setAttribute("uv2", new BufferAttribute(uv2, 2));

            geometry.computeBoundingSphere();
            geometry.computeBoundingBox();

            let chunkGrpMat =
                materialOverride ||
                this._createMaterial(chunkGrp.texId1, chunkGrp.texId2);

            const chunkMesh = new Mesh(geometry, chunkGrpMat);
            chunkMesh.name = "TER_" + this.name + "_" + i;
            chunkMesh.position.copy(
                this.position.clone().add(new Vector3(-80, -80, 0))
            );
            chunkMesh.updateMatrix();
            chunkMesh.matrixAutoUpdate = false;

            this.rootObj.add(chunkMesh);
            this.world.terChunks.push(chunkMesh);
            this.world.colObjects.push(chunkMesh);
        }
    }

    private _loadTerrain(callback: () => void): void {
        if (this.compiledTerrain) {
            const runtime = compiledChunkToRuntimeData(this.compiledTerrain);
            this.heightmap = runtime.heightmap;
            this.tilemap = runtime.tilemap;
            this.terrainTextures = runtime.textures;
            this.terrainTiles = runtime.tiles;
            this.lightmapTex = this._createCompiledLightmap(this.compiledTerrain);
            this._buildTerrain();
            callback();
            return;
        }
        const himPath = this.world.basePath + this.name + ".HIM";
        const tilPath = this.world.basePath + this.name + ".TIL";
        const ddsPath =
            this.world.basePath +
            this.name +
            "/" +
            this.name +
            "_PLANELIGHTINGMAP.DDS";
        const himRes = this.name + "_tilemap";
        const tilRes = this.name + "_heightmap";

        this.lightmapTex = TextureManager.load(ddsPath);
        this.lightmapTex.wrapS = ClampToEdgeWrapping;
        this.lightmapTex.wrapT = ClampToEdgeWrapping;
        this.lightmapTex.colorSpace = LinearSRGBColorSpace;

        this.world.DM.register(tilRes, TilemapData, tilPath);
        this.world.DM.register(himRes, HeightmapData, himPath);

        this.world.DM.get(himRes, tilRes, (heightmap: any, tilemap: any) => {
            this.heightmap = heightmap;
            this.tilemap = tilemap;
            this._buildTerrain();
            callback();
        });
    }

    private _createCompiledLightmap(chunk: CompiledTerrainChunk): DataTexture {
        let pixels = new Uint8Array([128, 128, 128, 255]);
        let width = 1;
        let height = 1;
        if (chunk.lightmap) {
            const decoded = atob(chunk.lightmap.rgbaBase64);
            pixels = Uint8Array.from(decoded, (character) => character.charCodeAt(0));
            width = chunk.lightmap.width;
            height = chunk.lightmap.height;
        }
        const texture = new DataTexture(pixels, width, height, RGBAFormat);
        texture.flipY = false;
        texture.wrapS = ClampToEdgeWrapping;
        texture.wrapT = ClampToEdgeWrapping;
        texture.colorSpace = LinearSRGBColorSpace;
        texture.needsUpdate = true;
        return texture;
    }

    private _loadObjects(callback: () => void): void {
        const litCnstPath =
            this.world.basePath +
            this.name +
            "/LIGHTMAP/BUILDINGLIGHTMAPDATA.LIT";
        const litDecoPath =
            this.world.basePath +
            this.name +
            "/LIGHTMAP/OBJECTLIGHTMAPDATA.LIT";
        const waitAll = new MultiWait();
        const lightmapWait = waitAll.one();

        LightmapManager.load(litCnstPath, (cnstLightmap: any) => {
            LightmapManager.load(litDecoPath, (decoLightmap: any) => {
                _loadChunkObjectGroup(
                    this,
                    "DECO_" + this.name,
                    this.info.objects,
                    this.world.decoModelMgr,
                    decoLightmap,
                    waitAll.one()
                );
                _loadChunkObjectGroup(
                    this,
                    "CNST_" + this.name,
                    this.info.buildings,
                    this.world.cnstModelMgr,
                    cnstLightmap,
                    waitAll.one()
                );
                _loadChunkAnimGroup(
                    this,
                    "ANIM_" + this.name,
                    this.info.animations,
                    waitAll.one()
                );
                lightmapWait();
            });
        });

        waitAll.wait(callback);
    }

    private _loadEffects(callback?: () => void): void {
        const waitAll = new MultiWait();

        for (let i = 0; i < this.info.effects.length; ++i) {
            const data = this.info.effects[i];
            const effectWait = waitAll.one();
            EffectManager.loadEffect(data.filePath, (effect: any) => {
                if (effect) {
                    effect.rootObj.position.copy(data.position);
                    effect.rootObj.quaternion.copy(data.rotation);
                    effect.rootObj.scale.copy(data.scale);
                    this.rootObj.add(effect.rootObj);
                    this.rootObj.add(effect.rootObj2);
                    effect.play();
                }
                effectWait();
            });
        }

        if (callback) waitAll.wait(callback);
    }

    private _loadWater(callback?: () => void): void {
        WaterEffect.reset();
        for (let i = 0; i < this.info.waterPlanes.length; ++i) {
            const plane = this.info.waterPlanes[i];
            const block = new OceanBlock(plane.start, plane.end);
            this.rootObj.add(block.load());
        }
        if (callback) callback();
    }

    _loadWarpGates(warps: ZoneWarpPoint[]): void {
        for (const warp of warps) {
            if (!warp.position || warp.warpId <= 0) continue;
            const gate = createWarpGateMesh();
            gate.position.copy(warp.position);
            gate.quaternion.copy(warp.rotation);
            gate.scale.copy(warp.scale);
            this.rootObj.add(gate);
        }
    }

    load(callback?: () => void): void {
        if (this.loadState === 2) {
            if (!this.isVisible) {
                this.world.rootObj.add(this.rootObj);
                this.isVisible = true;
            }
            if (callback) callback();
            return;
        }

        if (callback) {
            this.loadWaiters.push(callback);
        }

        if (this.loadState === 0) {
            this.world.rootObj.add(this.rootObj);
            this.isVisible = true;
            this.loadState = 1;

            const waitAll = new MultiWait();

            if (LOADTERRAIN) this._loadTerrain(waitAll.one());

            const finishInfoLoad = (info: ZoneChunkData) => {
                    this.info = info;
                    this.world.warpPoints.push(...info.warps);
                    this._loadWarpGates(info.warps);
                    if (!this.compiledTerrain) {
                        if (LOADOBJECTS) this._loadObjects(waitAll.one());
                        if (LOADEFFECTS) this._loadEffects();
                        if (LOADWATER) this._loadWater();
                    }

                    waitAll.wait(() => {
                        this.loadState = 2;
                        for (let i = 0; i < this.loadWaiters.length; ++i) {
                            this.loadWaiters[i]();
                        }
                        this.loadWaiters = [];
                    });
            };
            if (this.compiledTerrain) {
                finishInfoLoad(new ZoneChunkData());
            } else {
                ZoneChunkData.load(
                    this.world.basePath + this.name + ".IFO",
                    finishInfoLoad,
                );
            }
        }
    }

    markNotNeeded(): void {
        if (this.isVisible) {
            this.world.rootObj.remove(this.rootObj);
            this.isVisible = false;
        }
    }
}

function _loadChunkObjectGroup(
    chunk: WorldChunk,
    namePrefix: string,
    objList: any[],
    modelList: any,
    lightmap: any,
    callback: () => void
): void {
    const waitAll = new MultiWait();
    for (let i = 0; i < objList.length; ++i) {
        const objData = objList[i];
        const obj = modelList.createForStatic(
            objData.objectId,
            lightmap,
            i,
            waitAll.one()
        );
        if (!obj) continue;
        const lightmapData = lightmap?.data;
        const lightmapObject = lightmapData?.objects?.[i];
        if (lightmapObject) {
            obj.userData.legacyLightmap = {
                object: lightmapObject,
                textures: [...lightmapData.textures],
                textureNames: [...(lightmapData.textureNames ?? [])],
            };
        }
        obj.name = namePrefix + "_" + i;
        obj.position.copy(objData.position);
        obj.quaternion.copy(objData.rotation);
        obj.scale.copy(objData.scale);
        obj.updateMatrix();
        obj.matrixAutoUpdate = false;
        chunk.rootObj.add(obj);
        chunk.world.objects.push(obj);
        chunk.world.colObjects.push(obj);
    }
    waitAll.wait(callback);
}

function _loadChunkAnimGroup(
    chunk: WorldChunk,
    namePrefix: string,
    objList: any[],
    callback: () => void
): void {
    GDM.get("morph_anims", (morphAnims: any) => {
        for (let i = 0; i < objList.length; ++i) {
            const objData = objList[i];
            const obj = morphAnims.create(objData.objectId);
            obj.name = namePrefix + "_" + i;
            obj.position.copy(objData.position);
            obj.quaternion.copy(objData.rotation);
            obj.scale.copy(objData.scale);
            obj.updateMatrix();
            obj.matrixAutoUpdate = false;
            chunk.rootObj.add(obj);
        }
        callback();
    });
}
